// Paginagebruik (besluit Joris, 26 sep 2026): welke schermen worden gebruikt, door hoeveel
// mensen per dag en hoe lang. Bron: /Api/gebruik/overzicht (admin_settings). Rustig
// scherm: drie kerncijfers, één lijst, en welke menu-items niemand opende.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { leesJson, meldFout } from '../../../utils/apiFout';
import { NAV_ITEMS, SECONDARY_NAV_SECTIONS, resolveToolName } from '../../../config/navigation';
import { routeSjabloon } from '../../../utils/paginaGebruik';

/** Alle menu-schermen (hoofd-, sectie- en hubitems), zonder detailroutes. */
export function menuPaden() {
  const paden = new Set();
  NAV_ITEMS.forEach((i) => { if (!i.hidden && !i.path.includes(':')) paden.add(i.path); });
  Object.values(SECONDARY_NAV_SECTIONS).forEach((s) => (s.items || []).forEach((i) => paden.add(i.path)));
  return [...paden];
}

/** Leesbare naam: sectie-label (Mijn werk, Planning…) of paginatitel; anders het pad. */
export function labelVoor(pad) {
  const sectieItem = Object.values(SECONDARY_NAV_SECTIONS).flatMap((s) => s.items || [])
    .find((i) => i.path === pad && i.label !== 'Overzicht');
  if (pad === '(onbekend)') return 'Onbekend scherm';
  const titel = resolveToolName(pad.replace(/:[^/]+/g, 'x').replace(/\/\*$/, ''));
  if (titel) return titel;
  const hub = Object.values(SECONDARY_NAV_SECTIONS).find((s) => s.hubPath === pad);
  if (hub) return hub.label;
  return sectieItem ? sectieItem.label : pad;
}

/** Menu-items die in de periode door niemand zijn geopend. */
export function nooitBezocht(bezochteRoutes = [], paden = menuPaden()) {
  const gezien = new Set(bezochteRoutes);
  return paden.filter((p) => !gezien.has(routeSjabloon(p))).sort((a, b) => a.localeCompare(b));
}

export const duurTekst = (ms) => {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} u ${m % 60} min`;
};

const Kerncijfer = ({ label, waarde }) => (
  <Box sx={{ minWidth: 140 }}>
    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#2d3335' }}>{waarde}</Typography>
  </Box>
);

export default function Paginagebruik() {
  const [dagen, setDagen] = useState(30);
  const [data, setData] = useState(null);

  const laad = useCallback(async () => {
    setData(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/gebruik/overzicht?dagen=${dagen}`);
      setData(await leesJson(r, 'Paginagebruik laden'));
    } catch (e) {
      meldFout(e, 'Paginagebruik laden');
      setData({ routes: [], per_dag: [], weergaven: 0, fout: true });
    }
  }, [dagen]);

  useEffect(() => { laad(); }, [laad]);

  const routes = useMemo(() => data?.routes || [], [data]);
  const perDag = data?.per_dag || [];
  const gemGebruikers = perDag.length
    ? Math.round(perDag.reduce((t, d) => t + d.gebruikers, 0) / perDag.length) : 0;
  const maxGebruikers = Math.max(1, ...perDag.map((d) => d.gebruikers));
  const nooit = useMemo(() => nooitBezocht(routes.map((r) => r.route)), [routes]);

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Gemeten: welk scherm (als sjabloon, zonder id's of zoektermen), hoe lang je erop bleef,
        je rol en het uur. Niet gemeten: wie — geen naam of e-mailadres; een code die elke dag
        wisselt telt alleen hoeveel verschillende mensen er per dag waren. Geen cookies, geen
        externe partij; bewaard 180 dagen. Lokaal ontwikkelen telt niet mee.
      </Typography>

      <ToggleButtonGroup size="small" exclusive value={dagen} onChange={(e, v) => v && setDagen(v)} sx={{ mb: 2.5 }}>
        <ToggleButton value={7} sx={{ px: 1.5, textTransform: 'none' }}>7 dagen</ToggleButton>
        <ToggleButton value={30} sx={{ px: 1.5, textTransform: 'none' }}>30 dagen</ToggleButton>
      </ToggleButtonGroup>

      {data === null ? <CircularProgress size={22} /> : (
        <>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 3 }}>
            <Kerncijfer label="Paginaweergaven" waarde={(data.weergaven || 0).toLocaleString('nl-NL')} />
            <Kerncijfer label="Gebruikers per dag (gem.)" waarde={gemGebruikers} />
            <Kerncijfer label="Tijd per scherm (gem.)" waarde={duurTekst(data.gem_duur_ms)} />
          </Box>

          {perDag.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, mb: 1 }}>Unieke gebruikers per dag</Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 48 }} aria-label="Unieke gebruikers per dag">
                {perDag.map((d) => (
                  <Box key={d.datum} title={`${d.datum}: ${d.gebruikers} gebruikers, ${d.weergaven} weergaven`}
                    sx={{ flex: 1, maxWidth: 18, minHeight: 2, height: `${(d.gebruikers / maxGebruikers) * 100}%`, bgcolor: '#b9b3cc', borderRadius: '2px' }} />
                ))}
              </Box>
            </Box>
          )}

          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, mb: 0.5 }}>Meest bezochte schermen</Typography>
          {routes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Nog geen metingen in deze periode — het logje loopt vanaf 26 september 2026.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto', mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Scherm</TableCell>
                    <TableCell align="right">Weergaven</TableCell>
                    <TableCell align="right" title="Aantal (persoon × dag): wie drie dagen kwam telt drie keer">Gebruikersdagen</TableCell>
                    <TableCell align="right">Gem. tijd</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {routes.slice(0, 40).map((r) => {
                    const titel = labelVoor(r.route);
                    return (
                      <TableRow key={r.route} hover>
                        <TableCell>
                          {titel}
                          <Typography component="span" sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 1 }}>{r.route}</Typography>
                        </TableCell>
                        <TableCell align="right">{r.weergaven}</TableCell>
                        <TableCell align="right">{r.gebruikersdagen}</TableCell>
                        <TableCell align="right">{duurTekst(r.gem_duur_ms)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, mb: 0.5 }}>
            Menu-items die niemand opende ({nooit.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            {nooit.length ? nooit.map((p) => `${labelVoor(p)} (${p})`).join(' · ') : 'Alles is minstens één keer geopend.'}
          </Typography>
        </>
      )}
    </Box>
  );
}
